import * as React from 'react'
import {Suspense, useCallback, useEffect} from 'react'

import BuildLineLayout from '../../../../containers/BuildLineLayout'
import BuildsFetcher from '../../../../containers/BuildsFetcher'
import BuildsWithLastReadyLocator from '../../../../containers/BuildsWithLastReadyLocator'
import {BuildSelectionIdProvider} from '../../../../contexts/buildSelection'
import {useFetchEffect} from '../../../../hocs/withEffect'
import {
  useAgentPoolIdParam,
  useBooleanQueryParam,
  useSetQueryParams,
} from '../../../../hooks/routes'
import type {NormalizedBuild} from '../../../../types'
import {stringifyId} from '../../../../types'
import {subscribeonStatisticsUpdates} from '../../../../utils/subscriber'
import {getTopNavHelperData} from '../../../App/Header/Header.utils'
import type {StatisticsUpdateData} from '../../../App/Header/Links/Links.types'
import Builds from '../../../common/Builds/Builds'
import type {BuildsContainerProps} from '../../../common/Builds/Builds.container'
import type {BuildCustomRendererProps} from '../../../common/Builds/Builds.types'
import Loader from '../../../common/Loader/Loader'
import BuildPager from '../../../common/Pager/BuildPager/BuildPager.container'
import ServiceMessage from '../../../common/ServiceMessage/ServiceMessage'
import {QueueActions} from '../QueueActions/QueueActions'
import QueueBuild from '../QueueBuild/QueueBuild.container'
import QueueBuildDragLayer from '../QueueBuildDragLayer/QueueBuildDragLayer.container'
import QueueFilters from '../QueueFilters/QueueFilters'
import {getQueuePageLocator} from '../QueuePage.utils'

import {updateQueue$} from './QueueBuildList.streams'
import {useQueueBuildDragDrop} from './useQueueBuildDragDrop'

function BuildsRenderer({builds, ...restProps}: BuildsContainerProps) {
  const setQueryParams = useSetQueryParams()
  const onlyMyPersonal = useBooleanQueryParam('onlyMyPersonal')
  const [statistics, setStatistics] = React.useState<StatisticsUpdateData | null | undefined>(null)
  const {buildQueueSize = getTopNavHelperData().queue} = statistics ?? {}
  useFetchEffect(
    () =>
      subscribeonStatisticsUpdates((data: StatisticsUpdateData | null | undefined) =>
        setStatistics(data),
      ),
    [],
  )

  const {droppedBuildId, hoveredDropBuildId, setHoveredDropBuildId, dropBuilds, handleDrop} =
    useQueueBuildDragDrop(builds)

  const {alwaysShowLineBorders, wrapped, pathFilter} = restProps
  const renderBuild = useCallback(
    (build: NormalizedBuild, prevBuildId?: number, nextBuildId?: number) => (
      <QueueBuild
        key={stringifyId(build.id)}
        hoveredDropBuildId={hoveredDropBuildId}
        setHoveredDropBuildId={setHoveredDropBuildId}
        droppedBuildId={droppedBuildId}
        prevBuildId={prevBuildId}
        nextBuildId={nextBuildId}
        buildId={build.id}
        alwaysShowLineBorders={alwaysShowLineBorders || build.state === 'queued'}
        isQueued={build.state === 'queued'}
        wrapped={wrapped}
        pathFilter={pathFilter}
        onDrop={handleDrop}
      />
    ),
    [
      alwaysShowLineBorders,
      wrapped,
      pathFilter,
      hoveredDropBuildId,
      droppedBuildId,
      handleDrop,
      setHoveredDropBuildId,
    ],
  )

  if (builds.length === 0 && buildQueueSize !== 0 && !onlyMyPersonal && restProps.ready) {
    return (
      <ServiceMessage icon={'warning'}>
        {'You do not have permissions to see all of the items currently in the queue.'}
      </ServiceMessage>
    )
  } else {
    return (
      <>
        <QueueFilters
          onlyMyPersonal={onlyMyPersonal}
          setOnlyMyPersonal={on =>
            setQueryParams(({agentPoolId}) => ({
              agentPoolId,
              onlyMyPersonal: on ? 'true' : null,
            }))
          }
        />
        <Suspense>
          <QueueActions />
        </Suspense>
        <Suspense fallback={<Loader />}>
          <Builds builds={dropBuilds ?? builds} renderBuild={renderBuild} {...restProps} />
          <QueueBuildDragLayer wrapped={restProps.wrapped} pathFilter={restProps.pathFilter} />
        </Suspense>
      </>
    )
  }
}

function QueueBuildList() {
  const activeAgentPoolId = useAgentPoolIdParam()
  const onlyMyPersonal = useBooleanQueryParam('onlyMyPersonal')
  const locator = getQueuePageLocator(activeAgentPoolId, onlyMyPersonal)

  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') {
      window.scrollTo(0, 0)
    }
  }, [locator])

  return (
    <BuildsFetcher
      withQueued
      withPager
      updatePeriod={20000}
      customEndpoint={'/buildQueue'}
      locator={locator}
      update$={updateQueue$}
    >
      <BuildLineLayout showExactDuration={false} showComment={false}>
        <BuildSelectionIdProvider>
          <BuildsWithLastReadyLocator
            alwaysShowLineBorders
            showOverlayOnLoading
            withPager
            withLoader
            wrapped
            render={({builds, args}: BuildCustomRendererProps) => (
              <BuildsRenderer builds={builds} {...args} />
            )}
          />
        </BuildSelectionIdProvider>
      </BuildLineLayout>
      <BuildPager />
    </BuildsFetcher>
  )
}

export default React.memo(QueueBuildList)
